import React, { useRef, useEffect, useState } from "react";
import Chart from "chart.js/auto";
import axios from "axios";
import Cookies from "js-cookie";
import AppHeader from "../appHeader/appHeader";
import AppMenu from "../appMenu/appMenu";
import AppFooter from "../appFooter/appFooter";
import AppSetting from "../appSetting/appSetting";
import AppAdmin from "../appMenu/appAdmin";

/**
 * RelatorioCliente Component
 *
 * Este componente exibe relatórios de pagamentos para clientes e administradores.
 * Utiliza Chart.js para renderizar gráficos de barras e pizza com os dados obtidos da API.
 */
function RelatorioCliente() {
    // Estado para armazenar os valores dos pagamentos por tipo e mês
    const [valorPagamentos, setValorPagamentos] = useState({
        acordo: initializeMonthlyData(),
        apolice: initializeMonthlyData(),
        deposito: initializeMonthlyData(),
        custas: initializeMonthlyData(),
    });

    // Estado para armazenar o status dos pagamentos
    const [pagamentosStatus, setPagamentosStatus] = useState({
        realizado: 0,
        realizar: 0,
        atrasado: 0,
    });

    // Estado para gerenciar a autenticação do usuário
    const [authenticated, setAuthenticated] = useState(false);

    // Estado para armazenar os dados do usuário autenticado
    const [user, setUser] = useState(null);

    // Referências para os elementos canvas dos gráficos
    const chartContainerBar = useRef(null);
    const chartContainerPie = useRef(null);

    /**
     * Inicializa os dados mensais com valores zerados.
     * @returns {Object} Objeto com os meses como chaves e valores zerados.
     */
    function initializeMonthlyData() {
        const months = [
            "Janeiro",
            "Fevereiro",
            "Março",
            "Abril",
            "Maio",
            "Junho",
            "Julho",
            "Agosto",
            "Setembro",
            "Outubro",
            "Novembro",
            "Dezembro",
        ];
        const data = {};
        months.forEach((month) => {
            data[month] = 0;
        });
        return data;
    }

    /**
     * Verifica a autenticação do usuário ao montar o componente.
     * Redireciona para a página de login se a autenticação falhar.
     */
    useEffect(() => {
        axios
            .post("/api/auth", { withCredentials: true })
            .then((response) => {
                if (response.data.status === 200) {
                    setAuthenticated(true);
                    setUser(response.data.user);
                } else {
                    window.location.href = "/login";
                }
            })
            .catch(() => {
                window.location.href = "/login";
            });
    }, []);

    /**
     * Obtém os valores dos pagamentos por tipo e mês a partir da API.
     */
    useEffect(() => {
        if (authenticated) {
            axios
                .post(
                    "/api/get/value/payment",
                    { withCredentials: true }
                )
                .then((resp) => {
                    setValorPagamentos(resp.data);
                })
                .catch((error) => {
                    console.error(
                        "Erro ao obter valores dos pagamentos:",
                        error
                    );
                });
        }
    }, [authenticated]);

    /**
     * Obtém o status dos pagamentos a partir da API.
     */
    useEffect(() => {
        if (authenticated) {
            axios
                .post(
                    "/api/get/realization",
                    { withCredentials: true }
                )
                .then((resp) => {
                    setPagamentosStatus(resp.data);
                })
                .catch((error) => {
                    console.error(
                        "Erro ao obter status dos pagamentos:",
                        error
                    );
                });
        }
    }, [authenticated]);

    /**
     * Configura e renderiza o gráfico de barras com os dados dos pagamentos.
     */
    useEffect(() => {
        if (chartContainerBar.current && authenticated) {
            const chartDataBar = {
                labels: Object.keys(valorPagamentos.acordo),
                datasets: [
                    {
                        label: "$ Acordo",
                        data: Object.values(valorPagamentos.acordo),
                        borderWidth: 1,
                        backgroundColor: "#f2cc30",
                    },
                    {
                        label: "$ Apólice",
                        data: Object.values(valorPagamentos.apolice),
                        borderWidth: 1,
                        backgroundColor: "#b5cf48",
                    },
                    {
                        label: "$ Custas",
                        data: Object.values(valorPagamentos.custas),
                        borderWidth: 1,
                        backgroundColor: "#62b0b3",
                    },
                    {
                        label: "$ Depósito",
                        data: Object.values(valorPagamentos.deposito),
                        borderWidth: 1,
                        backgroundColor: "#5e539f",
                    },
                ],
            };

            const newChartInstanceBar = new Chart(chartContainerBar.current, {
                type: "bar",
                data: chartDataBar,
                options: {
                    scales: {
                        x: { stacked: true },
                        y: { stacked: true },
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                },
            });

            // Limpa a instância do gráfico ao desmontar o componente
            return () => {
                newChartInstanceBar.destroy();
            };
        }
    }, [valorPagamentos, authenticated]);

    /**
     * Configura e renderiza o gráfico de pizza com os dados do status dos pagamentos.
     */
    useEffect(() => {
        if (chartContainerPie.current && authenticated) {
            const chartDataPie = {
                labels: ["Realizado", "À Realizar", "Atrasado"],
                datasets: [
                    {
                        label: "Total",
                        data: [
                            pagamentosStatus.realizado,
                            pagamentosStatus.realizar,
                            pagamentosStatus.atrasado,
                        ],
                        borderWidth: 1,
                        backgroundColor: ["#36a2eb", "#ffcd56", "#ff6384"],
                    },
                ],
            };

            const newChartInstancePie = new Chart(chartContainerPie.current, {
                type: "pie",
                data: chartDataPie,
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                },
            });

            // Limpa a instância do gráfico ao desmontar o componente
            return () => {
                newChartInstancePie.destroy();
            };
        }
    }, [pagamentosStatus, authenticated]);

    // Estilos para o spinner de carregamento
    const spinnerStyle = {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
    };

    // Renderiza o spinner enquanto o usuário não está autenticado
    if (!authenticated) {
        return (
            <div style={spinnerStyle}>
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Cabeçalho da Página */}
            <section className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1>Relatórios</h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item">
                                    <a href="/">Início</a>
                                </li>
                                <li className="breadcrumb-item active">
                                    Relatórios
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </section>

            {/* Conteúdo Principal */}
            <section className="content">
                <div className="container-fluid">
                    <div className="row">
                        {/* Gráfico de Pagamentos */}
                        <div className="col-md-6">
                            <div className="card card-info">
                                <div className="card-header">
                                    <h3 className="card-title">Pagamentos</h3>
                                    <div className="card-tools">
                                        <button
                                            type="button"
                                            className="btn btn-tool"
                                            data-card-widget="collapse"
                                        >
                                            <i className="fas fa-minus" />
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-tool"
                                            data-card-widget="remove"
                                        >
                                            <i className="fas fa-times" />
                                        </button>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div
                                        className="chart-container"
                                        style={{
                                            position: "relative",
                                            height: "40vh",
                                            width: "100%",
                                        }}
                                    >
                                        <canvas ref={chartContainerBar} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Gráfico de Realização de Pagamentos */}
                        <div className="col-md-6">
                            <div className="card card-info">
                                <div className="card-header">
                                    <h3 className="card-title">
                                        Realização de Pagamentos
                                    </h3>
                                    <div className="card-tools">
                                        <button
                                            type="button"
                                            className="btn btn-tool"
                                            data-card-widget="collapse"
                                        >
                                            <i className="fas fa-minus" />
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-tool"
                                            data-card-widget="remove"
                                        >
                                            <i className="fas fa-times" />
                                        </button>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div
                                        className="chart-container"
                                        style={{
                                            position: "relative",
                                            height: "40vh",
                                            width: "100%",
                                        }}
                                    >
                                        <canvas ref={chartContainerPie} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

export default RelatorioCliente;
